/**
 * PSR Phase 2 — BLS local labour-market context (state unemployment series).
 */
import { fetchWithTimeout } from '../dataProviders/types';
import { factToCitationRef } from '../../lib/sentiment/citationRefs';
import { getProviderKey } from '../../lib/providerKeys';
import { resolveSentimentSiteContext } from './siteContext';
import {
  emptySentimentResult,
  type SentimentFetchOptions,
  type SentimentFetchResult,
  type SentimentFetcher,
} from './types';
import { warnUnkeyedSentimentFetcher } from './unkeyedApiKeyGuard';
import { probeHttpAvailability } from './probeAvailability';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';

const BLS_ENV_VAR = 'BLS_API_KEY'; // R-130-10
const BLS_PROBE_SERIES_ID = 'LNS14000000'; // R-130-10

/** State FIPS → BLS LAUS area code suffix (state only, seasonally adjusted). */
const STATE_LAUS_SUFFIX: Record<string, string> = {
  AL: '01', AK: '02', AZ: '04', AR: '05', CA: '06', CO: '08', CT: '09', DE: '10',
  DC: '11', FL: '12', GA: '13', HI: '15', ID: '16', IL: '17', IN: '18', IA: '19',
  KS: '20', KY: '21', LA: '22', ME: '23', MD: '24', MA: '25', MI: '26', MN: '27',
  MS: '28', MO: '29', MT: '30', NE: '31', NV: '32', NH: '33', NJ: '34', NM: '35',
  NY: '36', NC: '37', ND: '38', OH: '39', OK: '40', OR: '41', PA: '42', RI: '44',
  SC: '45', SD: '46', TN: '47', TX: '48', UT: '49', VT: '50', VA: '51', WA: '53',
  WV: '54', WI: '55', WY: '56',
};

function blsApiKey(): string | undefined {
  return getProviderKey('BLS');
}

export const blsFetcher: SentimentFetcher = {
  sourceId: 'bls',

  async probeAvailability() {
    const key = blsApiKey(); // R-130-10
    const body = {
      seriesid: [BLS_PROBE_SERIES_ID],
      startyear: '2025',
      endyear: '2025',
      ...(key ? { registrationkey: key } : {}),
    };
    return probeHttpAvailability({
      url: 'https://api.bls.gov/publicAPI/v2/timeseries/data/',
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      requiredEnvVarName: BLS_ENV_VAR,
      hasRequiredEnvVar: !!key,
      unconfiguredStatuses: [401, 403],
    });
  },

  async isAvailable(siteId, opts) {
    const ctx = await resolveSentimentSiteContext(siteId, opts);
    return !!(ctx?.state && STATE_LAUS_SUFFIX[ctx.state]);
  },

  async fetch(siteId, opts?: SentimentFetchOptions): Promise<SentimentFetchResult> {
    throwIfAborted(opts?.signal);
    try {
      const ctx = await resolveSentimentSiteContext(siteId, opts);
      if (!ctx?.state) {
        return emptySentimentResult('bls', ['bls: state code unavailable']);
      }
      const suffix = STATE_LAUS_SUFFIX[ctx.state];
      if (!suffix) {
        return emptySentimentResult('bls', [`bls: no LAUS series for state ${ctx.state}`]);
      }
      // LA + seasonal adjustment + 15-character area + 2-character measure.
      // https://www.bls.gov/help/hlpforma.htm#LA
      const areaCode = `ST${suffix}`.padEnd(15, '0');
      const seriesId = `LAS${areaCode}03`;
      const key = blsApiKey();
      if (!key) {
        warnUnkeyedSentimentFetcher('bls');
      }
      const getUrl = key
        ? `https://api.bls.gov/publicAPI/v2/timeseries/data/${seriesId}?registrationkey=${encodeURIComponent(key)}&latest=true`
        : `https://api.bls.gov/publicAPI/v2/timeseries/data/${seriesId}?latest=true`;
      const getRes = await fetchWithTimeout(getUrl, 15_000, opts?.signal);
      throwIfAborted(opts?.signal);
      if (!getRes.ok) {
        return emptySentimentResult('bls', [`bls: HTTP ${getRes.status}`]);
      }
      const json = await getRes.json() as {
        status?: string;
        Results?: { series?: Array<{ seriesID?: string; data?: Array<{
          value?: string;
          year?: string;
          period?: string;
          footnotes?: Array<{ code?: string; text?: string }>;
        }> }> };
        message?: string[];
      };

      if (json.status === 'REQUEST_NOT_PROCESSED' || json.status === 'REQUEST_FAILED') {
        const msg = (json.message ?? []).join('; ') || 'BLS request failed';
        return emptySentimentResult('bls', [`bls: ${msg}`]);
      }

      const series = json.Results?.series?.find(row => row.seriesID === seriesId);
      if (!series) {
        return emptySentimentResult('bls', ['bls: requested state series missing in response']);
      }
      // M13 is the annual average, not the latest monthly observation. Do not
      // infer a source month from retrieval time or an unordered response.
      const point = series.data?.filter(row =>
        /^\d{4}$/.test(row.year ?? '') && /^M(0[1-9]|1[0-2])$/.test(row.period ?? ''),
      ).sort((a, b) => `${b.year}-${b.period}`.localeCompare(`${a.year}-${a.period}`))[0];
      const rate = point?.value?.trim() ? Number(point.value) : NaN;
      if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
        return emptySentimentResult('bls', ['bls: valid monthly unemployment rate missing in response']);
      }

      const now = new Date().toISOString();
      const period = `${point!.year}-${point!.period!.slice(1)}`;
      const preliminary = point?.footnotes?.some(note =>
        note.code?.toUpperCase() === 'P' || /preliminary/i.test(note.text ?? ''),
      );
      const facts = [{
        key: 'state_unemployment_rate',
        label: `${ctx.state} state unemployment rate (${period}, seasonally adjusted${preliminary ? ', preliminary' : ''})`,
        value: rate,
        units: 'percent',
        url: `https://data.bls.gov/timeseries/${seriesId}`,
        observedAt: period,
      }];

      return {
        sourceId: 'bls',
        coverage: 'full',
        normalizedFacts: facts,
        citationRefs: [factToCitationRef(facts[0]!, 'bls', 1)],
        errors: [],
        fetchedAt: now,
      };
    } catch (e) {
      rethrowIfAborted(opts?.signal, e);
      return emptySentimentResult('bls', [`bls: ${e instanceof Error ? e.message : String(e)}`]);
    }
  },
};
